import { FormulaEvaluator } from '@shared/utils/formulaEvaluator';
import { evaluateNumericFormula } from '@shared/utils/quantityFormula';
import { PricingEngine } from '@shared/services/pricingEngine';
import { MatrixPricingEngine } from '@shared/services/matrixPricingEngine';
import { evaluateCPQOptionConditions } from '@shared/utils/conditionsEvaluator';
import { inheritParentOptionValues } from '@shared/utils/upsellUtils';
import {
  applyCatalogDiscountToPricingResult,
  getProductActiveDiscount,
} from '@shared/utils/productDiscount';

/**
 * useCPQPriceCalculator
 *
 * Unified pricing engine voor alle CPQ types:
 * 1. Legacy CPQ forms (met attributeMapping en priceRules)
 * 2. Nieuwe configurator forms (met selected-product data)
 *
 * Ondersteunt geavanceerde pricing:
 * - Per unit pricing: basePrice + (value × pricePerUnit)
 * - Fixed pricing: Vaste prijs ongeacht waarde
 * - Formula pricing: Custom berekeningen met {value}
 * - Matrix pricing: value × otherValue × pricePerUnit
 * - Cross-option rules: Staffels, bundels, conditionele pricing
 *
 * @param {Object} cpqConfig - De CPQ-configuratie uit form.settings.cpq
 * @param {Object} attributeValues - De actuele attributenwaarden (bv. gemapt uit formData)
 * @param {Object} [form] - Het formulierobject (voor FormulaEvaluator, optioneel)
 * @returns {Object} { breakdown, totalPrice, basePrice, optionsPrice, hiddenTotal, hiddenRules, shownRules }
 *
 * @see shared/services/pricingEngine.js - Core pricing calculation logic
 * @see docs/architecture/advanced-pricing-system.md - Full documentation
 *
 * KEEP IN SYNC with the backend (smartflow-api). This price ends up on the
 * quote (public forms and dashboard), and the backend recalculates it for
 * checks and as fallback, so any change to how base, matrix, delta, option,
 * formula or cross-option rule prices are calculated here must also be made in:
 *   - smartflow-api/services/productPricingService.js (calculateConfiguredProductPricing,
 *     applyCrossOptionRules)
 *   - smartflow-api/services/configuratorEnrichmentService.js (enrichProductOption,
 *     calculatePricingDeltaBreakdown)
 * Mismatches show up in the API logs as
 * "[ConfiguratorEnrichment] Frontend and backend price differ".
 */
export function useCPQPriceCalculator(cpqConfig, attributeValues, form) {
  // NIEUWE CONFIGURATOR LOGIC
  if (form?.type === 'configurator' && attributeValues['selected-product']) {
    return calculateConfiguratorPricing(attributeValues);
  }

  // LEGACY CPQ LOGIC (bestaande implementatie)
  return calculateLegacyCPQPricing(cpqConfig, attributeValues, form);
}

/**
 * Nieuwe configurator pricing logic
 *
 * Flow:
 * 1. Initialize PricingEngine
 * 2. Register alle product options met hun pricing config
 * 3. Register cross-option rules (staffels, bundels, etc.)
 * 4. Process elke optie:
 *    - Number options: Gebruik PricingEngine voor advanced pricing types
 *    - Choice options: Legacy pricing (choice.price)
 * 5. Apply cross-option rules via PricingEngine.calculateTotal()
 * 6. Return backend-compatible output structure
 *
 * Output structure compatible met legacy CPQ voor backend processing
 *
 * @param {Object} attributeValues - Form data met selected-product
 * @returns {Object} Backend-compatible pricing result
 */
const CONTENT_ONLY_TYPES = ['notice', 'image'];

/**
 * Bepaalt of een product-optie zichtbaar is (en dus meegerekend moet worden)
 * gegeven zijn `conditions` en de huidige formulierwaarden. Verborgen opties
 * (condities niet voldaan) mogen NIET geprijsd worden, anders lekken hun
 * default-waarden in het totaal.
 *
 * Wrappert de gedeelde `evaluateCPQOptionConditions` met de configurator-
 * specifieke fieldId-mapping (`${productId}-${optionId}-input`).
 *
 * @param {Object} option - Product option (met optionele `conditions`)
 * @param {Object} product - Het geselecteerde product
 * @param {Object} attributeValues - Huidige formulierwaarden
 * @returns {boolean} True als de optie geprijsd/getoond moet worden
 */
function isOptionConditionMet(option, product, attributeValues) {
  return evaluateCPQOptionConditions(option?.conditions, attributeValues, {
    resolveFieldId: (id) => `${product.id}-${id}-input`,
  });
}

function getOptionDisplayValue(option, value) {
  if (option?.type === 'single-choice') {
    const choice = option.choiceConfig?.choices?.find((c) => c.id === value);
    return choice?.label ?? value;
  }

  if (option?.type === 'multi-choice' && Array.isArray(value)) {
    return value
      .map((choiceId) => {
        const choice = option.choiceConfig?.choices?.find((c) => c.id === choiceId);
        return choice?.label ?? choiceId;
      })
      .join(', ');
  }

  if (option?.type === 'toggle') {
    const isOn = value === true || value === 'true';
    return isOn ? option.toggleConfig?.onLabel || 'Ja' : option.toggleConfig?.offLabel || 'Nee';
  }

  return value;
}

function getChoiceCalculationValue(option, value) {
  if (!option || value === undefined || value === null || value === '') return null;

  if (option.type === 'single-choice') {
    const choice = option.choiceConfig?.choices?.find((c) => c.id === value);
    const raw = choice?.calculation_value ?? choice?.calculationValue;
    if (raw === undefined || raw === null || raw === '') return null;
    const numeric = parseFloat(raw);
    return Number.isFinite(numeric) ? numeric : null;
  }

  if (option.type === 'multi-choice' && Array.isArray(value)) {
    return value.reduce((sum, choiceId) => {
      const choice = option.choiceConfig?.choices?.find((c) => c.id === choiceId);
      const raw = choice?.calculation_value ?? choice?.calculationValue;
      const numeric = parseFloat(raw);
      return sum + (Number.isFinite(numeric) ? numeric : 0);
    }, 0);
  }

  return null;
}

function buildPricingFormulaContext(product, attributeValues) {
  const context = {};

  product?.options?.forEach((option) => {
    const fieldId = `${product.id}-${option.id}-input`;
    const value = attributeValues[fieldId];
    if (value === undefined || value === null || value === '') return;

    const calcValue = getChoiceCalculationValue(option, value);
    context[option.id] = calcValue !== null ? calcValue : value;
    context[fieldId] = calcValue !== null ? calcValue : value;

    const displayValue = getOptionDisplayValue(option, value);
    if (displayValue !== value) {
      context[`${option.id}_label`] = displayValue;
    }
  });

  const computedFields = product?.computedFields || product?.computed_fields || [];
  if (Array.isArray(computedFields) && computedFields.length) {
    const evaluator = new FormulaEvaluator();
    computedFields.forEach((computedField) => {
      if (!computedField?.id || !computedField?.formula) return;
      context[computedField.id] =
        Number(evaluator.evaluateFormula(computedField.formula, context, null)) || 0;
    });
  }

  return context;
}

/**
 * Resolve a numeric choice-constraint dimension (number option or computed field).
 * Pass attributeValues with any temporary overrides (e.g. a candidate kapmaat)
 * so formulas like geleider + kap can be evaluated per choice.
 */
export function resolveConstraintDimensionValue(product, optionId, attributeValues) {
  if (!product || !optionId) return null;

  const directFieldId = `${product.id}-${optionId}-input`;
  const directRaw = attributeValues?.[directFieldId];
  if (directRaw !== undefined && directRaw !== null && directRaw !== '') {
    const directNum = parseFloat(directRaw);
    return Number.isFinite(directNum) ? directNum : null;
  }

  const computedFields = product.computedFields || product.computed_fields || [];
  const computed = computedFields.find((field) => field?.id === optionId);
  if (!computed?.formula) return null;

  const context = buildPricingFormulaContext(product, attributeValues);
  const value = Number(context[optionId]);
  return Number.isFinite(value) ? value : null;
}

function isConfigurableOption(option) {
  return option?.type && !CONTENT_ONLY_TYPES.includes(option.type);
}

/**
 * Price an upsell. Catalog `price` is used for simple extras. If the upsell
 * product itself has options, run the configurator engine on that product
 * (field ids are already `${upsellId}-${optionId}-input` in formData) so the
 * upsell line gets the configured total instead of €0 / base price.
 */
function resolveUpsellUnitPrice(upsellConfig, upsellProduct, attributeValues) {
  const source = { ...(upsellProduct || {}), ...(upsellConfig || {}) };
  const nestedId = source.productId || source.id;
  const options = source.options || [];
  const hasConfigurableOptions = options.some(isConfigurableOption);

  if (hasConfigurableOptions && nestedId) {
    const nestedProduct = { ...source, id: nestedId, options };
    const nestedValues = {
      ...attributeValues,
      ...inheritParentOptionValues(
        attributeValues['selected-product'],
        nestedProduct,
        attributeValues
      ),
    };
    const nestedResult = calculateConfiguratorPricing({
      ...nestedValues,
      'selected-product': {
        ...nestedProduct,
        name: source.name || source.title,
        crossOptionRules: source.crossOptionRules || source.cross_option_rules || [],
        autoIncludeRules: source.autoIncludeRules || source.auto_include_rules || [],
        computedFields: source.computedFields || source.computed_fields || [],
        useMatrixPricing: source.useMatrixPricing || source.use_matrix_pricing || false,
        pricingMatrix: source.pricingMatrix || source.pricing_matrix || null,
        upsells: [],
      },
      'product-selection-element': nestedId,
    });
    const nestedPrice = nestedResult?.productOnlyPrice ?? nestedResult?.totalPrice ?? 0;
    if (nestedPrice > 0) {
      return nestedPrice;
    }
  }

  return Number(source.price) || 0;
}

function getFormulaPricingConfig(source = {}) {
  const pricingType = source.pricingType || source.priceType || 'fixed';
  const formula = source.priceFormula || source.formula || '';
  return { pricingType, formula };
}

function calculateConfiguredPrice(source, fixedPrice, formulaContext, value = null) {
  const { pricingType, formula } = getFormulaPricingConfig(source);
  if (pricingType !== 'formula' || !formula) {
    return {
      price: Number(fixedPrice) || 0,
      calculation: null,
    };
  }

  const evaluator = new FormulaEvaluator();
  const context = {
    ...formulaContext,
    value: value ?? 0,
  };
  const price = Number(evaluator.evaluateFormula(formula, context, null)) || 0;

  return {
    price,
    calculation: {
      type: 'formula',
      expression: formula,
      result: price,
    },
  };
}

function formatMatrixDisplayValue(product, matrixResult) {
  const dimensionValues = matrixResult?.dimensionValues || {};
  const computedFields = product?.computedFields || product?.computed_fields || [];
  const dimensions = Object.entries(dimensionValues).map(([optionId, value]) => {
    const option = product?.options?.find((o) => o.id === optionId);
    const computed = computedFields.find((field) => field?.id === optionId);
    const label = option?.label || computed?.label || optionId;
    return `${label}: ${value}`;
  });
  const rounded = (matrixResult?.tierAdjustments || [])
    .map((item) => `${item.dimension}: ${item.original} → ${item.rounded}`)
    .join(', ');

  return [
    dimensions.length ? `Matrix: ${dimensions.join(', ')}` : '',
    rounded ? `afgerond: ${rounded}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

function calculateConfiguratorPricing(attributeValues) {
  const selectedProduct = attributeValues['selected-product'];

  if (!selectedProduct) {
    return {
      breakdown: [],
      totalPrice: 0,
      productOnlyPrice: 0,
      upsellsItems: [],
      upsellsPrice: 0,
      basePrice: 0,
      optionsPrice: 0,
      hiddenTotal: 0,
      hiddenRules: [],
      shownRules: [],
    };
  }

  const formulaContext = buildPricingFormulaContext(selectedProduct, attributeValues);
  const matrixConfiguration = { ...attributeValues, ...formulaContext };

  // ✅ CHECK FOR PRODUCT-LEVEL MATRIX PRICING
  if (selectedProduct.useMatrixPricing && selectedProduct.pricingMatrix) {
    const matrixEngine = new MatrixPricingEngine();
    const matrixResult = matrixEngine.calculatePrice(selectedProduct, matrixConfiguration);
    const deltaResult = matrixEngine.calculateDeltaTables(selectedProduct, matrixConfiguration);

    if (!matrixResult.available) {
      const unavailableResult = {
        breakdown: [],
        totalPrice: 0,
        productOnlyPrice: 0,
        upsellsItems: [],
        upsellsPrice: 0,
        basePrice: 0,
        optionsPrice: 0,
        error: matrixResult.error,
        available: false,
        tierAdjustments: matrixResult.tierAdjustments || [],
      };
      return unavailableResult;
    }

    // Get matrix dimension option IDs (these are handled by matrix, not as separate options)
    const matrixDimensionIds = [];
    if (selectedProduct.pricingMatrix?.dimensions) {
      // Handle both grid format (object) and cells format (array)
      if (Array.isArray(selectedProduct.pricingMatrix.dimensions)) {
        // Cells format: array of dimension objects
        selectedProduct.pricingMatrix.dimensions.forEach((dim) => {
          if (dim.optionId) matrixDimensionIds.push(dim.optionId);
        });
      } else if (typeof selectedProduct.pricingMatrix.dimensions === 'object') {
        // Grid format: object with row, col, group
        if (selectedProduct.pricingMatrix.dimensions.row) {
          matrixDimensionIds.push(selectedProduct.pricingMatrix.dimensions.row);
        }
        if (selectedProduct.pricingMatrix.dimensions.col) {
          matrixDimensionIds.push(selectedProduct.pricingMatrix.dimensions.col);
        }
        if (selectedProduct.pricingMatrix.dimensions.group) {
          matrixDimensionIds.push(selectedProduct.pricingMatrix.dimensions.group);
        }
      }
    }

    // Calculate OTHER options (not matrix dimensions)
    let otherOptionsPrice = 0;
    const otherOptionsBreakdown = [];
    const hiddenRules = [];
    const shownRules = [];

    if (selectedProduct.options) {
      selectedProduct.options.forEach((option) => {
        // Skip content-only options (no pricing)
        if (CONTENT_ONLY_TYPES.includes(option.type)) return;

        // Skip matrix dimension options - they're already in matrixPrice
        if (matrixDimensionIds.includes(option.id)) {
          return;
        }

        // Skip options whose visibility conditions are NOT met. Conditional
        // options (e.g. the "Aantal ..." number fields tied to a multi-choice)
        // carry default values that would otherwise be priced even though the
        // user never sees or selects them.
        if (!isOptionConditionMet(option, selectedProduct, attributeValues)) {
          return;
        }

        const fieldId = `${selectedProduct.id}-${option.id}-input`;
        const userValue = attributeValues[fieldId];

        if (userValue !== undefined && userValue !== null && userValue !== '') {
          // Check multi-choice condition explicitly
          const isMultiChoice = option.type === 'multi-choice';
          const hasShowOnQuote = option.showOnQuote === true;
          const isArray = Array.isArray(userValue);
          const hasLength = isArray && userValue.length > 0;

          // Handle multi-choice with showOnQuote: true - each choice gets separate line
          if (isMultiChoice && hasShowOnQuote && isArray && hasLength) {
            // Process each choice separately
            userValue.forEach((choiceId) => {
              const choice = option.choiceConfig?.choices?.find((c) => c.id === choiceId);
              if (choice) {
                const { price: choicePrice, calculation } = calculateConfiguredPrice(
                  choice,
                  choice.price || 0,
                  formulaContext,
                  1
                );
                const choiceLabel = choice.label || choiceId;
                const displayValue = choiceLabel;

                // Always add to breakdown if showOnQuote is true, even if price is 0
                // But only add to price if price is not 0
                if (choicePrice !== 0) {
                  otherOptionsPrice += choicePrice;
                }

                // Always add to breakdown and shownRules if showOnQuote is true
                const ruleObj = {
                  label: choiceLabel,
                  value: choicePrice,
                  type: 'add',
                  displayValue,
                  action: { type: 'add', value: choicePrice },
                  calculation,
                  rule: {
                    label: choiceLabel,
                    showInQuote: true, // Always show since showOnQuote is true
                  },
                };

                shownRules.push(ruleObj);

                const breakdownItem = {
                  type: 'option',
                  id: option.id,
                  choiceId: choice.id,
                  label: option.label,
                  displayValue: choiceLabel,
                  value: choicePrice,
                  price: choicePrice,
                  quantity: 1,
                  calculation,
                  showOnQuote: true,
                  hideOnQuote: option.hideOnQuote || false,
                  internalOnly: option.internalOnly || false,
                  imageUrl: choice.image || null,
                };

                otherOptionsBreakdown.push(breakdownItem);
              }
            });
          } else {
            // Normal handling: single-choice, number, or multi-choice without showOnQuote
          let optionPrice = 0;
          let optionLabel = option.label;
          let displayValue = userValue;
          let optionCalculation = null;

          if (option.type === 'single-choice' || option.type === 'multi-choice') {
              // For multi-choice without showOnQuote, sum all choice prices
              if (option.type === 'multi-choice' && Array.isArray(userValue)) {
                let totalPrice = 0;
                const choiceLabels = [];
                userValue.forEach((choiceId) => {
                  const choice = option.choiceConfig?.choices?.find((c) => c.id === choiceId);
                  if (choice) {
                    totalPrice += calculateConfiguredPrice(
                      choice,
                      choice.price || 0,
                      formulaContext,
                      1
                    ).price;
                    choiceLabels.push(choice.label || choice.content || choiceId);
                  }
                });
                optionPrice = totalPrice;
                displayValue = choiceLabels.join(', ');
                optionLabel = option.label;
              } else {
                // Single choice
            const choice = option.choiceConfig?.choices?.find((c) => c.id === userValue);
            if (choice) {
              const { price, calculation } = calculateConfiguredPrice(
                choice,
                choice.price || 0,
                formulaContext,
                1
              );
              optionPrice = price;
              optionCalculation = calculation;
              displayValue = choice.label;
              optionLabel = choice.label;
                }
            }
          } else if (option.type === 'number') {
            // Only if not a matrix dimension
            const pricingType = option.numberConfig?.pricingType || 'per_unit';
            if (pricingType === 'formula' || pricingType === 'fixed') {
              const { price, calculation } = calculateConfiguredPrice(
                option.numberConfig,
                option.numberConfig?.fixedPrice || 0,
                formulaContext,
                userValue
              );
              optionPrice = price;
              optionCalculation = calculation;
            } else {
              const pricePerUnit = option.numberConfig?.pricePerUnit || 0;
              optionPrice = parseFloat(userValue) * pricePerUnit;
            }
            displayValue = `${userValue} ${option.label.toLowerCase()}`;
          } else if (option.type === 'toggle') {
            const isOn = userValue === true || userValue === 'true';
            const { price, calculation } = isOn
              ? calculateConfiguredPrice(
                  option.toggleConfig,
                  option.toggleConfig?.price || 0,
                  formulaContext,
                  1
                )
              : { price: 0, calculation: null };
            optionPrice = price;
            optionCalculation = calculation;
            displayValue = isOn
              ? option.toggleConfig?.onLabel || 'Ja'
              : option.toggleConfig?.offLabel || 'Nee';
            optionLabel = option.label;
          }

          if (optionPrice !== 0) {
            otherOptionsPrice += optionPrice;

            // Create rule object compatible with legacy CPQ structure
            const ruleObj = {
              label: optionLabel,
              value: optionPrice,
              type: 'add',
              displayValue,
              action: { type: 'add', value: optionPrice },
              calculation: optionCalculation || null,
              rule: {
                label: optionLabel,
                showInQuote: option.showOnQuote || false,
              },
            };

            // Add to appropriate array based on showOnQuote
            if (option.showOnQuote) {
              shownRules.push(ruleObj);
            } else {
              hiddenRules.push(ruleObj);
            }

              let choiceIdFields = {};
              if (option.type === 'multi-choice' && Array.isArray(userValue) && userValue.length) {
                choiceIdFields = { choiceIds: [...userValue] };
              } else if (
                option.type === 'single-choice' &&
                userValue !== undefined &&
                userValue !== null &&
                userValue !== ''
              ) {
                choiceIdFields = { choiceId: userValue };
              }

              const breakdownItem = {
              type: 'option',
              id: option.id,
              ...choiceIdFields,
              label: option.label,
              displayValue,
              value: optionPrice,
              price: optionPrice,
              quantity: 1,
              calculation: optionCalculation || null,
              showOnQuote: option.showOnQuote || false,
              hideOnQuote: option.hideOnQuote || false,
              internalOnly: option.internalOnly || false,
              };

              otherOptionsBreakdown.push(breakdownItem);
            }
          }
        }
      });
    }

    // ✅ PROCESS AUTO-INCLUDE RULES
    const autoIncludeItems = processAutoIncludeRules(
      selectedProduct,
      attributeValues,
      hiddenRules,
      shownRules
    );

    // ✅ PROCESS UPSELLS
    // `upsellsItems` exposes each chosen upsell as a structured entry with its
    // own pricesIncludeVat / vatRate so consumers (cart UI, submission,
    // backend) can treat upsells as separate proposal items instead of folding
    // them into the parent product's totalPrice in a single VAT dimension.
    let upsellsPrice = 0;
    const upsellsBreakdown = [];
    const upsellsItems = [];
    const upsellsFieldId = `${selectedProduct.id}-upsells-input`;
    
    // Collect all upsell fields (for categorized upsells, there might be multiple)
    // Could be: `${productId}-upsells-input` or `${productId}-upsells-${categoryId}-input`
    const allUpsellFields = [];
    Object.keys(attributeValues).forEach((key) => {
      if (key === upsellsFieldId || (key.startsWith(`${selectedProduct.id}-upsells-`) && key.endsWith('-input'))) {
        const value = attributeValues[key];
        if (Array.isArray(value) && value.length > 0) {
          allUpsellFields.push(value);
        }
      }
    });

    // Combine all upsell arrays and deduplicate
    const allSelectedUpsellIds = [];
    const seenUpsellIds = new Set();
    
    allUpsellFields.forEach((upsellArray) => {
      upsellArray.forEach((upsellItem) => {
        const upsellId = typeof upsellItem === 'object' && upsellItem.id ? upsellItem.id : upsellItem;
        if (!seenUpsellIds.has(upsellId)) {
          seenUpsellIds.add(upsellId);
          allSelectedUpsellIds.push(upsellItem);
        }
      });
    });

    if (allSelectedUpsellIds.length > 0) {
      // Get upsell configs from the selected product object
      // Can be old format (string[]) or new format (ProductUpsell[])
      const upsellConfigs = selectedProduct.upsells || [];

      // Handle both new format (array of objects) and old format (array of IDs)
      const upsellItems =
        allSelectedUpsellIds.length > 0 &&
        typeof allSelectedUpsellIds[0] === 'object' &&
        allSelectedUpsellIds[0].id
          ? allSelectedUpsellIds // New format: array of objects
          : allSelectedUpsellIds.map((id) => ({ id, quantity: 1 })); // Old format: convert to objects

      upsellItems.forEach((upsellItem) => {
        const upsellId = typeof upsellItem === 'object' ? upsellItem.id : upsellItem;
        const quantity =
          typeof upsellItem === 'object' && upsellItem.quantity !== undefined
            ? upsellItem.quantity
            : 1;
        // Find upsell config (new format) or product (old format)
        let upsellConfig = null;
        let upsellProduct = null;

        // Check if new format (ProductUpsell objects)
        if (
          upsellConfigs.length > 0 &&
          typeof upsellConfigs[0] === 'object' &&
          upsellConfigs[0].id
        ) {
          upsellConfig = upsellConfigs.find((u) => u.id === upsellId || u.productId === upsellId);
          // In new format, product data might be in config or we need to find it
          if (upsellConfig && upsellConfig.name) {
            // Product data is cached in config
            upsellProduct = {
              id: upsellConfig.id || upsellConfig.productId,
              name: upsellConfig.name,
              price: upsellConfig.price,
              sku: upsellConfig.sku || upsellConfig.base_sku,
              imageUrl: upsellConfig.imageUrl || upsellConfig.image_url || null,
            };
          } else {
            // Fallback: find in upsellProducts array (if available)
            upsellProduct = upsellConfigs.find(
              (u) => u.id === upsellId || u.productId === upsellId
            );
          }
        } else {
          // Old format: upsellConfigs are just product objects or IDs
          upsellProduct = upsellConfigs.find((u) => {
            if (typeof u === 'object') {
              return u.id === upsellId || u.productId === upsellId;
            }
            return u === upsellId;
          });
        }

        if (!upsellProduct) {
          return; // Skip if product not found
        }

        // Validate and clamp quantity (if allowQuantity is enabled)
        let finalQuantity = quantity;
        if (upsellConfig?.allowQuantity) {
          if (upsellConfig.quantityConfig) {
            finalQuantity = Math.max(upsellConfig.quantityConfig.min || 1, finalQuantity);
            finalQuantity = Math.min(upsellConfig.quantityConfig.max || 999, finalQuantity);
          }
        } else {
          // If allowQuantity is false, force quantity to 1
          finalQuantity = 1;
        }

        const unitPrice = resolveUpsellUnitPrice(upsellConfig, upsellProduct, attributeValues);
        const totalPrice = unitPrice * finalQuantity;
        upsellsPrice += totalPrice;

        // displayValue should only contain the name, not the quantity
        // The quantity is shown separately in the quote table
        const displayValue = upsellProduct.name || 'Extra optie';

        // Resolve BTW info per upsell. Prefer values on the upsell product
        // itself, then values cached on the upsell config, then fall back to
        // parent product. `null` is preserved (= follow org default) so the
        // consumer can apply the same resolver as for the parent product.
        const upsellPricesIncludeVat =
          upsellProduct.pricesIncludeVat ??
          upsellConfig?.pricesIncludeVat ??
          null;
        const upsellVatRate =
          upsellProduct.vatRate ??
          upsellConfig?.vatRate ??
          selectedProduct.vatRate ??
          21;

        upsellsBreakdown.push({
          type: 'upsell',
          label: upsellProduct.name || 'Extra optie',
          displayValue: displayValue,
          value: totalPrice,
          price: unitPrice,
          quantity: finalQuantity,
          sku: upsellProduct.sku || upsellProduct.base_sku,
          showOnQuote: upsellConfig?.showOnQuote !== false, // Default to true if not set
          showOnPackingSlip: upsellConfig?.showOnPackingSlip || false,
          // Product info for backend
          productId: upsellProduct.id || upsellProduct.productId || null,
          imageUrl: upsellProduct.imageUrl || upsellProduct.image_url || null,
          pricesIncludeVat: upsellPricesIncludeVat,
          vatRate: upsellVatRate,
        });

        upsellsItems.push({
          productId: upsellProduct.id || upsellProduct.productId || null,
          name: upsellProduct.name || 'Extra optie',
          sku: upsellProduct.sku || upsellProduct.base_sku || null,
          imageUrl: upsellProduct.imageUrl || upsellProduct.image_url || null,
          unitPrice,
          quantity: finalQuantity,
          lineTotal: totalPrice,
          pricesIncludeVat: upsellPricesIncludeVat,
          vatRate: upsellVatRate,
          showOnQuote: upsellConfig?.showOnQuote !== false,
          showOnPackingSlip: upsellConfig?.showOnPackingSlip || false,
          showDescriptionOnQuote: upsellConfig?.showDescriptionOnQuote === true,
          description: upsellConfig?.showDescriptionOnQuote
            ? upsellProduct.description || upsellConfig.description || ''
            : '',
        });
      });
    }

    // Calculate base prices FIRST (needed for cross-option rules)
    const deltaPrice = Number(deltaResult?.totalDelta || 0);
    const deltaBreakdown = (deltaResult?.items || [])
      .filter((item) => item.value !== 0)
      .map((item) => ({
        type: 'delta',
        id: item.id,
        label: item.label,
        displayValue: item.matchSource === 'default' ? `${item.label} (fallback)` : item.label,
        value: item.value,
        price: item.value,
        quantity: 1,
        showOnQuote: true,
      }));

    deltaBreakdown.forEach((item) => {
      shownRules.push({
        label: item.label,
        value: item.value,
        type: 'add',
        displayValue: item.displayValue,
        action: { type: 'add', value: item.value },
        rule: {
          label: item.label,
          showInQuote: true,
        },
      });
    });

    const basePrice = selectedProduct.price || 0;
    const basePriceWithMatrix = basePrice + matrixResult.price + deltaPrice;

    // ✅ PROCESS CROSS-OPTION RULES (matrix path)
    // Convert attributeValues to format expected by evaluateConditionGroup
    const ruleAttributeValues = {};

    selectedProduct.options?.forEach((option) => {
      const fieldId = `${selectedProduct.id}-${option.id}-input`;
      const userValue = attributeValues[fieldId];
      if (userValue !== undefined && userValue !== null && userValue !== '') {
        ruleAttributeValues[option.id] = userValue;
        ruleAttributeValues[fieldId] = userValue;
      }
    });

    ruleAttributeValues.option_count =
      selectedProduct.options?.filter((option) => {
        const fieldId = `${selectedProduct.id}-${option.id}-input`;
        const userValue = attributeValues[fieldId];
        return userValue !== undefined && userValue !== null && userValue !== '';
      }).length || 0;

    // ✅ EVALUATE COMPUTED FIELDS
    if (selectedProduct.computedFields && Array.isArray(selectedProduct.computedFields)) {
      const evaluator = new FormulaEvaluator();
      

      
      selectedProduct.computedFields.forEach((computedField) => {
        if (computedField.id && computedField.formula) {
          try {

            
            // Log which values will be substituted
            const placeholders = computedField.formula.match(/\{([^}]+)\}/g) || [];
            placeholders.forEach(placeholder => {
              const key = placeholder.slice(1, -1);
            });
            
            const computedValue = evaluator.evaluateFormula(
              computedField.formula,
              ruleAttributeValues,
              null
            );
            
            const finalValue = Number(computedValue) || 0;
            ruleAttributeValues[computedField.id] = finalValue;
            
          } catch (error) {
            console.warn(`    ❌ Failed to compute field ${computedField.id}:`, error);
            ruleAttributeValues[computedField.id] = 0;
          }
        }
      });
      
    }

    let crossOptionRulesPrice = 0;
    const crossOptionRulesBreakdown = [];

    // Calculate original total before cross-option rules (for matrix path)
    const totalBeforeRulesMatrix =
      basePriceWithMatrix +
      otherOptionsPrice +
      autoIncludeItems.totalPrice +
      upsellsPrice;

    // Process cross-option rules
    if (selectedProduct.crossOptionRules && Array.isArray(selectedProduct.crossOptionRules)) {
      selectedProduct.crossOptionRules.forEach((rule, ruleIndex) => {
        const ruleLabel = rule.label || `Rule ${ruleIndex + 1}`;
        const currentTotalForConditions =
          basePriceWithMatrix +
          otherOptionsPrice +
          autoIncludeItems.totalPrice +
          upsellsPrice +
          crossOptionRulesPrice;
        const ruleEvaluationValues = {
          ...ruleAttributeValues,
          subtotal: totalBeforeRulesMatrix,
          total: currentTotalForConditions,
          current_total: currentTotalForConditions,
        };

        // Evaluate conditions
        const match = rule.rootGroup
          ? evaluateConditionGroup(rule.rootGroup, ruleEvaluationValues, null, null)
          : true;

        const conditionsStr =
          rule.rootGroup?.rules
            ?.map((r) => `${r.attribute} ${r.operator} ${r.value}`)
            .join(' AND ') || 'geen';

        if (match) {
          rule.actions?.forEach((action, actionIndex) => {
            const beforeTotal =
              basePriceWithMatrix +
              otherOptionsPrice +
              autoIncludeItems.totalPrice +
              upsellsPrice +
              crossOptionRulesPrice;
            let value = 0;
            let label = rule.label || '';
            let type = action.type;

            if (action.type === 'add') {
              value = Number(action.value) || 0;
              crossOptionRulesPrice += value;
            } else if (action.type === 'subtract') {
              value = -Number(action.value) || 0;
              crossOptionRulesPrice += value;
            } else if (action.type === 'discount') {
              const currentTotal =
                basePriceWithMatrix +
                otherOptionsPrice +
                autoIncludeItems.totalPrice +
                upsellsPrice +
                crossOptionRulesPrice;
              value = currentTotal * (Number(action.value) / 100) * -1;
              crossOptionRulesPrice += value;
            } else if (action.type === 'multiply') {
              const currentTotal =
                basePriceWithMatrix +
                otherOptionsPrice +
                autoIncludeItems.totalPrice +
                upsellsPrice +
                crossOptionRulesPrice;
              value = currentTotal * (Number(action.value) || 1) - currentTotal;
              crossOptionRulesPrice += value;
            } else if (action.type === 'override') {
              const currentTotal =
                basePriceWithMatrix +
                otherOptionsPrice +
                autoIncludeItems.totalPrice +
                upsellsPrice +
                crossOptionRulesPrice;
              value = Number(action.value) - currentTotal;
              crossOptionRulesPrice += value;
            } else if (action.type === 'formula') {
              const currentTotal =
                basePriceWithMatrix +
                otherOptionsPrice +
                autoIncludeItems.totalPrice +
                upsellsPrice +
                crossOptionRulesPrice;
              // For matrix path: calculate original total before cross-option rules
              const totalBeforeRules =
                basePriceWithMatrix +
                otherOptionsPrice +
                autoIncludeItems.totalPrice +
                upsellsPrice;
              const formulaContext = {
                ...ruleAttributeValues,
                subtotal: totalBeforeRules,  // Original value before any cross-option rules
                total: currentTotal,         // Cumulative value including previous rules
              };

              const evaluator = new FormulaEvaluator();
              value =
                Number(evaluator.evaluateFormula(action.expression, formulaContext, null)) || 0;
              crossOptionRulesPrice += value;
            }

            const afterTotal =
              basePriceWithMatrix +
              otherOptionsPrice +
              autoIncludeItems.totalPrice +
              upsellsPrice +
              crossOptionRulesPrice;

            // Build displayValue based on action type
            let displayValue = label;
            if (action.type === 'formula' && action.expression) {
              displayValue = `${label} (${action.expression})`;
            } else if (action.type === 'add' || action.type === 'subtract') {
              displayValue = `${label} (${action.value})`;
            } else if (action.type === 'discount') {
              displayValue = `${label} (${action.value}%)`;
            } else if (action.type === 'multiply') {
              displayValue = `${label} (×${action.value})`;
            } else if (action.type === 'override') {
              displayValue = `${label} (€${action.value})`;
            }

            const ruleObj = {
              label,
              value,
              type,
              displayValue,
              action,
              rule,
            };

            // Add to breakdown (same structure as options/upsells)
            crossOptionRulesBreakdown.push({
              type: 'rule',
              label: rule.label,
              displayValue: displayValue,
              value: value,
              price: value,
              quantity: 1,
              showOnQuote: rule.showInQuote !== false,
              action: action,
              rule: rule,
            });

            if (rule.showInQuote) {
              shownRules.push(ruleObj);
            } else {
              hiddenRules.push(ruleObj);
            }
          });
        }
      });
    }

    // Final summary for matrix path
    const totalPrice =
      basePriceWithMatrix +
      otherOptionsPrice +
      autoIncludeItems.totalPrice +
      upsellsPrice +
      crossOptionRulesPrice;

    const breakdown = [
      {
        type: 'product',
        label: selectedProduct.title || selectedProduct.name,
        displayValue: formatMatrixDisplayValue(selectedProduct, matrixResult),
        value: basePriceWithMatrix, // Base price + matrix price
        price: basePriceWithMatrix,
        quantity: 1,
        calculation: {
          type: 'matrix',
          dimensionValues: matrixResult.dimensionValues || {},
          tierAdjustments: matrixResult.tierAdjustments || [],
          matrixPrice: matrixResult.price,
          basePrice,
          deltaPrice,
        },
      },
      ...deltaBreakdown,
      ...otherOptionsBreakdown,
      ...autoIncludeItems.breakdown,
      ...upsellsBreakdown,
      ...crossOptionRulesBreakdown, // Cross-option rules added to breakdown
    ];

    const result = {
      breakdown,
      totalPrice,
      // `productOnlyPrice` is the parent product's price WITHOUT upsells folded in.
      // Each upsell is exposed separately via `upsellsItems[]` with its own
      // pricesIncludeVat/vatRate, so consumers can render and submit upsells
      // as standalone proposal items. `totalPrice` still includes upsells for
      // backwards compatibility with code that expects a single rolled-up total.
      productOnlyPrice: totalPrice - upsellsPrice,
      basePrice, // Just product.price (zonder matrix)
      matrixPrice: matrixResult.price, // Separate field voor backend/debugging
      deltaPrice, // Sum of optional lookup delta tables
      deltaItems: deltaResult?.items || [],
      optionsPrice: otherOptionsPrice, // Other options (handzender, kleur, etc.)
      autoIncludePrice: autoIncludeItems.totalPrice, // Auto-include items total
      autoIncludeItems: autoIncludeItems.items, // For backend/pakbon
      upsellsPrice: upsellsPrice, // Upsells total price (sum across upsellsItems)
      upsellsItems, // Structured upsells with per-item BTW info
      crossOptionRulesPrice: crossOptionRulesPrice, // Cross-option rules total price
      hiddenTotal: totalPrice,
      hiddenRules,
      shownRules,
      matrixResult,
      available: true,
    };

    return applyCatalogDiscountToPricingResult(
      result,
      getProductActiveDiscount(selectedProduct)
    );
  }

  // Initialize pricing engine for advanced pricing calculations
  const pricingEngine = new PricingEngine();

  let basePrice = selectedProduct.price || 0;
  let optionsPrice = 0;
  const breakdown = [];
  const hiddenRules = []; // Voor items die niet op quote getoond worden
  const shownRules = []; // Voor items die wel op quote getoond worden

  // Register all options with pricing engine
  if (selectedProduct.options) {
    selectedProduct.options.forEach((option) => {
      if (CONTENT_ONLY_TYPES.includes(option.type)) return;
      pricingEngine.registerOption(option.id, option.numberConfig || {});
    });
  }

  // Register cross-option rules
  if (selectedProduct.crossOptionRules) {
    selectedProduct.crossOptionRules.forEach((rule) => {
      pricingEngine.addRule(rule);
    });
  }

  // Process product options with new pricing engine
  if (selectedProduct.options) {
    selectedProduct.options.forEach((option) => {
      if (CONTENT_ONLY_TYPES.includes(option.type)) return;

      // Skip options whose visibility conditions are NOT met (see matrix path).
      if (!isOptionConditionMet(option, selectedProduct, attributeValues)) {
        return;
      }

      const fieldId = `${selectedProduct.id}-${option.id}-input`;
      const userValue = attributeValues[fieldId];

      if (userValue !== undefined && userValue !== null && userValue !== '') {
        let optionPrice = 0;
        let optionLabel = option.label;
        let displayValue = userValue;
        let optionCalculation = null;
        // Set to true when an option has already aggregated itself into
        // optionsPrice/breakdown/shown|hiddenRules (e.g. multi-choice with
        // showOnQuote which pushes one row per choice). Prevents double counting.
        let handledInline = false;

        if (option.type === 'number') {
          const pricingType = option.numberConfig?.pricingType || 'per_unit';
          const pricePerUnit = option.numberConfig?.pricePerUnit || 0;

          if (pricingType === 'matrix') {
            const otherValues = {};
            selectedProduct.options.forEach((otherOption) => {
              const otherFieldId = `${selectedProduct.id}-${otherOption.id}-input`;
              const otherUserValue = attributeValues[otherFieldId];
              if (
                otherUserValue !== undefined &&
                otherUserValue !== null &&
                otherUserValue !== ''
              ) {
                if (otherOption.type === 'single-choice' || otherOption.type === 'multi-choice') {
                  const choice = findChoiceByValue(otherOption, otherUserValue);
                  if (choice) {
                    otherValues[otherOption.id] = choice.label;
                  }
                } else {
                  otherValues[otherOption.id] = otherUserValue;
                }
              }
            });

            optionPrice = pricingEngine.calculateOptionPrice(option.id, userValue, otherValues);
          } else {
            optionPrice = pricingEngine.calculateOptionPrice(option.id, userValue);
          }

          if (pricingType === 'formula' && option.numberConfig?.formula) {
            optionCalculation = {
              type: 'formula',
              expression: option.numberConfig.formula,
              result: optionPrice,
            };
          }

          displayValue = `${userValue} ${option.label.toLowerCase()}`;
        } else if (option.type === 'single-choice' || option.type === 'multi-choice') {
          // Multi-choice with showOnQuote: emit one row per selected choice
          if (
            option.type === 'multi-choice' &&
            option.showOnQuote === true &&
            Array.isArray(userValue) &&
            userValue.length > 0
          ) {
            handledInline = true;

            userValue.forEach((choiceId) => {
              const choice = option.choiceConfig?.choices?.find((c) => c.id === choiceId);
              if (choice) {
                const { price: choicePrice, calculation } = calculateConfiguredPrice(
                  choice,
                  choice.price || 0,
                  formulaContext,
                  1
                );
                const choiceLabel = choice.label || choiceId;

                if (choicePrice !== 0) {
                  optionsPrice += choicePrice;
                }

                const ruleObj = {
                  label: choiceLabel,
                  value: choicePrice,
                  type: 'add',
                  displayValue: choiceLabel,
                  action: { type: 'add', value: choicePrice },
                  calculation,
                  rule: {
                    label: choiceLabel,
                    showInQuote: true,
                  },
                };

                shownRules.push(ruleObj);

                breakdown.push({
                  type: 'option',
                  id: option.id,
                  choiceId: choice.id,
                  label: option.label,
                  displayValue: choiceLabel,
                  value: choicePrice,
                  price: choicePrice,
                  quantity: 1,
                  calculation,
                  showOnQuote: true,
                  hideOnQuote: option.hideOnQuote || false,
                  imageUrl: choice.image || null,
                });
              }
            });
          } else if (option.type === 'multi-choice' && Array.isArray(userValue)) {
            // Multi-choice without showOnQuote: sum all choice prices into one row
            let totalPrice = 0;
            const choiceLabels = [];
            userValue.forEach((choiceId) => {
              const choice = option.choiceConfig?.choices?.find((c) => c.id === choiceId);
              if (choice) {
                totalPrice += calculateConfiguredPrice(
                  choice,
                  choice.price || 0,
                  formulaContext,
                  1
                ).price;
                choiceLabels.push(choice.label || choice.content || choiceId);
              }
            });
            optionPrice = totalPrice;
            displayValue = choiceLabels.join(', ');
            optionLabel = option.label;
          } else {
            const choice = findChoiceByValue(option, userValue);
            if (choice) {
              const { price, calculation } = calculateConfiguredPrice(
                choice,
                choice.price || 0,
                formulaContext,
                1
              );
              optionPrice = price;
              optionCalculation = calculation;
              displayValue = choice.label;
              optionLabel = choice.label;
            }
          }

        } else if (option.type === 'toggle') {
          const isOn = userValue === true || userValue === 'true';
          const { price, calculation } = isOn
            ? calculateConfiguredPrice(
                option.toggleConfig,
                option.toggleConfig?.price || 0,
                formulaContext,
                1
              )
            : { price: 0, calculation: null };
          optionPrice = price;
          optionCalculation = calculation;
          displayValue = isOn
            ? option.toggleConfig?.onLabel || 'Ja'
            : option.toggleConfig?.offLabel || 'Nee';
          optionLabel = option.label;
        }

        // Unified aggregation: runs for both number and (non-inline) choice options.
        // Matches the structure used by the matrix path.
        if (!handledInline) {
          const ruleObj = {
            label: optionLabel,
            value: optionPrice,
            type: 'add',
            displayValue,
            action: { type: 'add', value: optionPrice },
            calculation: optionCalculation,
            rule: {
              label: optionLabel,
              showInQuote: option.showOnQuote || false,
            },
          };

          if (option.showOnQuote) {
            shownRules.push(ruleObj);
          } else {
            hiddenRules.push(ruleObj);
          }

          optionsPrice += optionPrice;

          let choiceIdFields = {};
          if (option.type === 'multi-choice' && Array.isArray(userValue) && userValue.length) {
            choiceIdFields = { choiceIds: [...userValue] };
          } else if (
            option.type === 'single-choice' &&
            userValue !== undefined &&
            userValue !== null &&
            userValue !== ''
          ) {
            choiceIdFields = { choiceId: userValue };
          }

          breakdown.push({
            type: 'option',
            id: option.id,
            ...choiceIdFields,
            label: option.label,
            displayValue,
            value: optionPrice,
            price: optionPrice,
            quantity: 1,
            calculation: optionCalculation,
            showOnQuote: option.showOnQuote || false,
            hideOnQuote: option.hideOnQuote || false,
          });
        }
      }
    });
  }

  // Use pricing engine to calculate total with cross-option rules
  const allValues = {};
  selectedProduct.options?.forEach((option) => {
    const fieldId = `${selectedProduct.id}-${option.id}-input`;
    const userValue = attributeValues[fieldId];
    if (userValue !== undefined && userValue !== null && userValue !== '') {
      allValues[option.id] = userValue;
    }
  });

  // ✅ PROCESS AUTO-INCLUDE RULES (non-matrix path)
  const autoIncludeItems = processAutoIncludeRules(
    selectedProduct,
    attributeValues,
    hiddenRules,
    shownRules
  );

  // ✅ PROCESS UPSELLS (non-matrix path)
  // See matrix-path comment above for structure rationale of upsellsItems.
  let upsellsPrice = 0;
  const upsellsBreakdown = [];
  const upsellsItems = [];
  const upsellsFieldId = `${selectedProduct.id}-upsells-input`;
  
  // Collect all upsell fields (for categorized upsells, there might be multiple)
  // Could be: `${productId}-upsells-input` or `${productId}-upsells-${categoryId}-input`
  const allUpsellFields = [];
  Object.keys(attributeValues).forEach((key) => {
    if (key === upsellsFieldId || (key.startsWith(`${selectedProduct.id}-upsells-`) && key.endsWith('-input'))) {
      const value = attributeValues[key];
      if (Array.isArray(value) && value.length > 0) {
        allUpsellFields.push(value);
      }
    }
  });

  // Combine all upsell arrays and deduplicate
  const allSelectedUpsells = [];
  const seenUpsellIds = new Set();
  
  allUpsellFields.forEach((upsellArray) => {
    upsellArray.forEach((upsellItem) => {
      const upsellId = typeof upsellItem === 'object' && upsellItem.id ? upsellItem.id : upsellItem;
      if (!seenUpsellIds.has(upsellId)) {
        seenUpsellIds.add(upsellId);
        allSelectedUpsells.push(upsellItem);
      }
    });
  });

  if (allSelectedUpsells.length > 0) {
    // Get upsell configs from the selected product object
    const upsellConfigs = selectedProduct.upsells || [];

    // Handle both new format (array of objects) and old format (array of IDs)
    const upsellItems =
      allSelectedUpsells.length > 0 && typeof allSelectedUpsells[0] === 'object' && allSelectedUpsells[0].id
        ? allSelectedUpsells // New format: array of objects
        : allSelectedUpsells.map((id) => ({ id, quantity: 1 })); // Old format: convert to objects

    upsellItems.forEach((upsellItem) => {
      const upsellId = typeof upsellItem === 'object' ? upsellItem.id : upsellItem;
      const quantity =
        typeof upsellItem === 'object' && upsellItem.quantity !== undefined
          ? upsellItem.quantity
          : 1;

      // Find upsell config (new format) or product (old format)
      let upsellConfig = null;
      let upsellProduct = null;

      // Check if new format (ProductUpsell objects)
      if (upsellConfigs.length > 0 && typeof upsellConfigs[0] === 'object' && upsellConfigs[0].id) {
        upsellConfig = upsellConfigs.find((u) => u.id === upsellId || u.productId === upsellId);
        if (upsellConfig && upsellConfig.name) {
          upsellProduct = {
            id: upsellConfig.id || upsellConfig.productId,
            name: upsellConfig.name,
            price: upsellConfig.price,
            sku: upsellConfig.sku || upsellConfig.base_sku,
            imageUrl: upsellConfig.imageUrl || upsellConfig.image_url || null,
          };
        } else {
          upsellProduct = upsellConfigs.find((u) => u.id === upsellId || u.productId === upsellId);
        }
      } else {
        // Old format
        upsellProduct = upsellConfigs.find((u) => {
          if (typeof u === 'object') {
            return u.id === upsellId || u.productId === upsellId;
          }
          return u === upsellId;
        });
      }

      if (!upsellProduct) {
        return;
      }

      // Validate and clamp quantity (if allowQuantity is enabled)
      let finalQuantity = quantity;
      if (upsellConfig?.allowQuantity) {
        if (upsellConfig.quantityConfig) {
          finalQuantity = Math.max(upsellConfig.quantityConfig.min || 1, finalQuantity);
          finalQuantity = Math.min(upsellConfig.quantityConfig.max || 999, finalQuantity);
        }
      } else {
        // If allowQuantity is false, force quantity to 1
        finalQuantity = 1;
      }

      const unitPrice = resolveUpsellUnitPrice(upsellConfig, upsellProduct, attributeValues);
      const totalPrice = unitPrice * finalQuantity;
      upsellsPrice += totalPrice;

      // displayValue should only contain the name, not the quantity
      // The quantity is shown separately in the quote table
      const displayValue = upsellProduct.name || 'Extra optie';

      const upsellPricesIncludeVat =
        upsellProduct.pricesIncludeVat ??
        upsellConfig?.pricesIncludeVat ??
        null;
      const upsellVatRate =
        upsellProduct.vatRate ??
        upsellConfig?.vatRate ??
        selectedProduct.vatRate ??
        21;

      upsellsBreakdown.push({
        type: 'upsell',
        label: upsellProduct.name || 'Extra optie',
        displayValue: displayValue,
        value: totalPrice,
        price: unitPrice,
        quantity: finalQuantity,
        sku: upsellProduct.sku || upsellProduct.base_sku,
        showOnQuote: upsellConfig?.showOnQuote !== false,
        showOnPackingSlip: upsellConfig?.showOnPackingSlip || false,
        // Product info for backend
        productId: upsellProduct.id || upsellProduct.productId || null,
        imageUrl: upsellProduct.imageUrl || upsellProduct.image_url || null,
        pricesIncludeVat: upsellPricesIncludeVat,
        vatRate: upsellVatRate,
      });

      upsellsItems.push({
        productId: upsellProduct.id || upsellProduct.productId || null,
        name: upsellProduct.name || 'Extra optie',
        sku: upsellProduct.sku || upsellProduct.base_sku || null,
        imageUrl: upsellProduct.imageUrl || upsellProduct.image_url || null,
        unitPrice,
        quantity: finalQuantity,
        lineTotal: totalPrice,
        pricesIncludeVat: upsellPricesIncludeVat,
        vatRate: upsellVatRate,
        showOnQuote: upsellConfig?.showOnQuote !== false,
        showOnPackingSlip: upsellConfig?.showOnPackingSlip || false,
        showDescriptionOnQuote: upsellConfig?.showDescriptionOnQuote === true,
        description: upsellConfig?.showDescriptionOnQuote
          ? upsellProduct.description || upsellConfig.description || ''
          : '',
      });
    });
  }

  // Calculate total price including cross-option rules, auto-includes, and upsells
  // In non-matrix path, we calculate optionsPrice manually, so use that instead of pricingEngine
  let totalPrice = basePrice + optionsPrice + autoIncludeItems.totalPrice + upsellsPrice;
  
  // Store the original totalPrice BEFORE cross-option rules for {subtotal} in formulas
  const totalPriceBeforeRules = totalPrice;

  // ✅ PROCESS CROSS-OPTION RULES (non-matrix path)

  // Convert attributeValues to format expected by evaluateConditionGroup
  const ruleAttributeValues = {};

  selectedProduct.options?.forEach((option) => {
    const fieldId = `${selectedProduct.id}-${option.id}-input`;
    const userValue = attributeValues[fieldId];
    if (userValue !== undefined && userValue !== null && userValue !== '') {
      ruleAttributeValues[option.id] = userValue;
      ruleAttributeValues[fieldId] = userValue;
    }
  });

  // Add special fields for formula evaluation
  ruleAttributeValues.option_count =
    selectedProduct.options?.filter((option) => {
      const fieldId = `${selectedProduct.id}-${option.id}-input`;
      const userValue = attributeValues[fieldId];
      return userValue !== undefined && userValue !== null && userValue !== '';
    }).length || 0;

  // ✅ EVALUATE COMPUTED FIELDS
  if (selectedProduct.computedFields && Array.isArray(selectedProduct.computedFields)) {
    const evaluator = new FormulaEvaluator();
    selectedProduct.computedFields.forEach((computedField) => {
      if (computedField.id && computedField.formula) {
        try {
          const computedValue = evaluator.evaluateFormula(
            computedField.formula,
            ruleAttributeValues,
            null
          );
          ruleAttributeValues[computedField.id] = Number(computedValue) || 0;
        } catch (error) {
          console.warn(`Failed to compute field ${computedField.id}:`, error);
          ruleAttributeValues[computedField.id] = 0;
        }
      }
    });
  }

  let crossOptionRulesPrice = 0;

  // Process cross-option rules
  if (selectedProduct.crossOptionRules && Array.isArray(selectedProduct.crossOptionRules)) {
    selectedProduct.crossOptionRules.forEach((rule, ruleIndex) => {
      const ruleEvaluationValues = {
        ...ruleAttributeValues,
        subtotal: totalPriceBeforeRules,
        total: totalPrice,
        current_total: totalPrice,
      };
      const match = rule.rootGroup
        ? evaluateConditionGroup(rule.rootGroup, ruleEvaluationValues, null, null)
        : true;

      if (match) {
        rule.actions?.forEach((action, actionIndex) => {
          let value = 0;
          let label = rule.label || '';
          let type = action.type;

          if (action.type === 'add') {
            value = Number(action.value) || 0;
            totalPrice += value;
          } else if (action.type === 'subtract') {
            value = -Number(action.value) || 0;
            totalPrice += value;
          } else if (action.type === 'discount') {
            value = totalPrice * (Number(action.value) / 100) * -1;
            totalPrice += value;
          } else if (action.type === 'multiply') {
            value = totalPrice * (Number(action.value) || 1) - totalPrice;
            totalPrice += value;
          } else if (action.type === 'override') {
            value = Number(action.value) - totalPrice;
            totalPrice = Number(action.value);
          } else if (action.type === 'formula') {
            // For formula: {subtotal} = original price before cross-option rules
            //              {total} = cumulative price including previous rules
            const formulaContext = {
              ...ruleAttributeValues,
              subtotal: totalPriceBeforeRules,
              total: totalPrice,
            };

            const evaluator = new FormulaEvaluator();
            value = Number(evaluator.evaluateFormula(action.expression, formulaContext, null)) || 0;
            totalPrice += value;
          }

          crossOptionRulesPrice += value;

          // Build displayValue based on action type
          let displayValue = label;
          if (action.type === 'formula' && action.expression) {
            // For formula actions, show the expression as part of displayValue
            displayValue = `${label} (${action.expression})`;
          } else if (action.type === 'add' || action.type === 'subtract') {
            displayValue = `${label} (${action.value})`;
          } else if (action.type === 'discount') {
            displayValue = `${label} (${action.value}%)`;
          } else if (action.type === 'multiply') {
            displayValue = `${label} (×${action.value})`;
          } else if (action.type === 'override') {
            displayValue = `${label} (€${action.value})`;
          }

          const ruleObj = {
            label,
            value,
            type,
            displayValue,
            action,
            rule,
          };

          if (rule.showInQuote) {
            shownRules.push(ruleObj);
          } else {
            hiddenRules.push(ruleObj);
          }
        });
      }
    });
  }

  // hiddenTotal = basePrice + alle hiddenRules (zoals legacy CPQ)
  const hiddenTotal = hiddenRules.reduce((sum, r) => sum + (r.value || 0), basePrice);

  // Build cross-option rules breakdown (same structure as options/upsells)
  const crossOptionRulesBreakdown = [
    ...shownRules
      .filter((r) => r.rule?.id?.startsWith('rule_'))
      .map((rule) => ({
        type: 'rule',
        label: rule.label,
        displayValue: rule.displayValue || rule.label,
        value: rule.value || 0,
        price: rule.value || 0,
        quantity: 1,
        showOnQuote: true,
        action: rule.action,
        rule: rule.rule,
      })),
    ...hiddenRules
      .filter((r) => r.rule?.id?.startsWith('rule_'))
      .map((rule) => ({
        type: 'rule',
        label: rule.label,
        displayValue: rule.displayValue || rule.label,
        value: rule.value || 0,
        price: rule.value || 0,
        quantity: 1,
        showOnQuote: false,
        action: rule.action,
        rule: rule.rule,
      })),
  ];

  const finalBreakdown = [
      // Base product altijd in breakdown voor frontend display
      {
        type: 'product',
        label: selectedProduct.name,
        value: basePrice,
        price: basePrice,
        quantity: 1,
      },
    // Use the breakdown items we already built during option processing
    // (these already include multi-choice with showOnQuote as separate items)
    ...breakdown,
      // Auto-include items
      ...autoIncludeItems.breakdown,
      // Upsells
      ...upsellsBreakdown,
      // Cross-option rules (both hidden and shown) - same structure as options/upsells
      ...crossOptionRulesBreakdown,
  ];

  const result = {
    // Legacy CPQ compatible fields (voor backend)
    basePrice,
    hiddenTotal, // ← Backend gebruikt dit voor hoofdregel
    hiddenRules, // ← Regels die niet op quote staan
    shownRules, // ← Regels die wel op quote staan
    totalPrice,
    // Parent product price WITHOUT upsells folded in. Upsells live in
    // `upsellsItems[]` with their own BTW context.
    productOnlyPrice: totalPrice - upsellsPrice,
    autoIncludePrice: autoIncludeItems.totalPrice, // Auto-include items total
    autoIncludeItems: autoIncludeItems.items, // For backend/pakbon
    upsellsPrice: upsellsPrice, // Upsells total price (sum across upsellsItems)
    upsellsItems, // Structured upsells with per-item BTW info
    crossOptionRulesPrice: crossOptionRulesPrice, // Cross-option rules total price

    // Extra fields voor frontend/debugging
    // Build final breakdown: use the breakdown array we built during processing
    breakdown: finalBreakdown,
    optionsPrice,
    currency: 'EUR',
    productName: selectedProduct.name,
    calculatedAt: new Date().toISOString(),
  };

  return applyCatalogDiscountToPricingResult(
    result,
    getProductActiveDiscount(selectedProduct)
  );
}

/**
 * Legacy CPQ pricing logic (bestaande implementatie)
 */
function calculateLegacyCPQPricing(cpqConfig, attributeValues, form) {
  const evaluator = new FormulaEvaluator();
  const basePrice = cpqConfig?.basePrice ?? 0;
  const priceRules = Array.isArray(cpqConfig?.priceRules) ? cpqConfig.priceRules : [];
  let price = basePrice;
  const lines = [];
  const hiddenRules = [];
  const shownRules = [];

  priceRules.forEach((rule) => {
    const match = rule.rootGroup
      ? evaluateConditionGroup(rule.rootGroup, attributeValues, form, cpqConfig?.attributeMapping)
      : true;
    if (match) {
      rule.actions.forEach((action) => {
        let value = 0;
        let label = rule.label || '';
        let type = action.type;
        if (action.type === 'add') {
          value = Number(action.value);
          price += value;
        } else if (action.type === 'subtract') {
          value = -Number(action.value);
          price -= Number(action.value);
        } else if (action.type === 'discount') {
          value = price * (Number(action.value) / 100) * -1;
          price += value; // value is negative
        } else if (action.type === 'override') {
          value = Number(action.value) - price;
          price = Number(action.value);
        } else if (action.type === 'formula') {
          value = Number(evaluator.evaluateFormula(action.expression, attributeValues, form));
          price += value;
        }
        const ruleObj = { label, value, type, action, rule };
        if (rule.showInQuote) {
          shownRules.push(ruleObj);
          // Push als object ipv string
          lines.push(ruleObj);
        } else {
          hiddenRules.push(ruleObj);
        }
      });
    }
  });

  // Som van basePrice + alle hiddenRules
  const hiddenTotal = hiddenRules.reduce((sum, r) => sum + (r.value || 0), basePrice);

  return {
    basePrice,
    hiddenRules,
    shownRules,
    breakdown: lines,
    totalPrice: price,
    hiddenTotal,
    optionsPrice: price - basePrice, // Voor consistentie met configurator
  };
}

/**
 * Vergelijkt de waarde uit attributeValues met de waarde uit de prijsregel.
 * Voor keuzevelden worden beide kanten (actuele waarde en rule.value) gemapt naar de feed value via de vraag-choices.
 */
function evaluateConditionGroup(group, values, form, attributeMapping) {
  const ruleResults = group.rules.map((rule) => {
    if (!rule.attribute) return false;
    let val = values[rule.attribute];
    let compareValue = rule.value;
    // Map beide kanten naar de feed value (voor keuzevelden)
    if (form && rule.attribute) {
      val = mapChoiceIdToFeedValue(rule.attribute, val, form, attributeMapping);
      compareValue = mapFeedValueToChoiceId(rule.attribute, rule.value, form, attributeMapping);
      // Waarschuw als de value niet voorkomt
      const element = findFormElementByAttribute(form, rule.attribute, attributeMapping);
      if (element && element.props && element.props.choices) {
        const isValid = element.props.choices.some((c) => c.value === compareValue);
        if (!isValid) {
          console.warn(
            `[CPQ] Waarschuwing: rule.value "${rule.value}" (mapped: "${compareValue}") voor attribuut "${rule.attribute}" is geen geldige keuze-value. Gebruik altijd de value uit de feed-optie!`
          );
        }
      }
    }
    let result;
    switch (rule.operator) {
      case '==':
        result = val == compareValue;
        break;
      case '!=':
        result = val != compareValue;
        break;
      case '>':
        result = Number(val) > Number(compareValue);
        break;
      case '>=':
        result = Number(val) >= Number(compareValue);
        break;
      case '<':
        result = Number(val) < Number(compareValue);
        break;
      case '<=':
        result = Number(val) <= Number(compareValue);
        break;
      default:
        result = false;
    }
    return result;
  });
  const subGroupResults = (group.subGroups || []).map((sub) =>
    evaluateConditionGroup(sub, values, form, attributeMapping)
  );
  const all = [...ruleResults, ...subGroupResults];
  return group.logic === 'AND' ? all.every(Boolean) : all.some(Boolean);
}

/**
 * Maakt het mogelijk om in prijsregels voor keuzevelden zowel de feed value als de vraag-choice.id te gebruiken.
 * - attributeKey: de attribute uit de feed (zoals 'type_glas')
 * - feedValue: de value uit de feed-optie, of eventueel het label
 * - form: het formulierobject
 * - attributeMapping: de mapping van attribute naar vraagId
 * @returns de juiste vraag-choice.value indien gevonden, anders de originele feedValue
 */
function mapFeedValueToChoiceId(attributeKey, feedValue, form, attributeMapping) {
  if (!form || !attributeMapping) return feedValue;
  const map = attributeMapping.find((m) => m.attribute === attributeKey);
  if (!map) return feedValue;
  const questionId = map.questionId;
  let element = null;
  for (const page of form.pages || []) {
    if (page.elements) {
      element = page.elements.find((e) => e.id === questionId);
      if (element) break;
    }
  }
  if (!element || !element.props || !element.props.choices) return feedValue;
  // Zoek de choice waar cpq_value, value, content, label of id overeenkomt
  const choice = element.props.choices.find(
    (c) =>
      c.cpq_value === feedValue ||
      c.value === feedValue ||
      c.content === feedValue ||
      c.label === feedValue ||
      c.id === feedValue
  );
  // Geef de cpq_value als die bestaat, anders value, anders id
  return choice ? choice.cpq_value || choice.value || choice.id : feedValue;
}

/**
 * Maakt het mogelijk om de actuele waarde (keuze-ID) uit formData te mappen naar de feed value.
 * - attributeKey: de attribute uit de feed (zoals 'type_glas')
 * - choiceId: de keuze-ID uit formData
 * - form: het formulierobject
 * - attributeMapping: de mapping van attribute naar vraagId
 * @returns de juiste feed value indien gevonden, anders de originele choiceId
 */
function mapChoiceIdToFeedValue(attributeKey, choiceId, form, attributeMapping) {
  if (!form || !attributeMapping) return choiceId;
  const map = attributeMapping.find((m) => m.attribute === attributeKey);
  if (!map) return choiceId;
  const questionId = map.questionId;
  let element = null;
  for (const page of form.pages || []) {
    if (page.elements) {
      element = page.elements.find((e) => e.id === questionId);
      if (element) break;
    }
  }
  if (!element || !element.props || !element.props.choices) return choiceId;
  const choice = element.props.choices.find((c) => c.id === choiceId);
  // Geef de cpq_value als die bestaat, anders value, anders id
  return choice ? choice.cpq_value || choice.value || choice.id : choiceId;
}

/**
 * Helper function to find a choice by its value/ID
 */
function findChoiceByValue(option, userValue) {
  if (!option.choiceConfig?.choices) return null;

  // Handle generated choice IDs (uitval_in_cm-choice-2)
  if (userValue.includes('-choice-')) {
    const choiceIndex = parseInt(userValue.split('-choice-')[1]);
    return option.choiceConfig.choices[choiceIndex];
  } else {
    return option.choiceConfig.choices.find((c) => c.id === userValue);
  }
}

function findFormElementByAttribute(form, attributeKey, attributeMapping) {
  if (!form || !form.settings || !form.settings.cpq || !attributeMapping) return null;
  const mapping = attributeMapping;
  const map = mapping.find((m) => m.attribute === attributeKey);
  if (!map) return null;
  const questionId = map.questionId;
  for (const page of form.pages || []) {
    if (page.elements) {
      const el = page.elements.find((e) => e.id === questionId);
      if (el) return el;
    }
  }
  return null;
}

/**
 * Process auto-include rules for a product
 * Evaluates conditions and adds items to the order
 *
 * @param {Object} product - The selected product with autoIncludeRules
 * @param {Object} attributeValues - Current form values
 * @param {Array} hiddenRules - Array to push hidden rules to
 * @param {Array} shownRules - Array to push shown rules to
 * @returns {Object} { items, breakdown, totalPrice }
 */
function processAutoIncludeRules(product, attributeValues, hiddenRules, shownRules) {
  const result = {
    items: [],
    breakdown: [],
    totalPrice: 0,
  };

  // Check both camelCase and snake_case
  const rules = product?.autoIncludeRules || product?.auto_include_rules;

  
  if (!rules || !Array.isArray(rules) || rules.length === 0) {
    return result;
  }

  // Log all available attribute values for debugging

  rules.forEach((rule, index) => {

    // Check if rule should be applied
    const shouldApply = evaluateAutoIncludeConditions(rule, product, attributeValues);

    if (!shouldApply) {

      return;
    }

    // Calculate quantity
    let quantity = 1;

    
    if (rule.quantityType === 'calculated' && rule.quantityFormula) {
      quantity = evaluateQuantityFormula(rule.quantityFormula, product, attributeValues);
    } else if (rule.quantity === 'calculated' && rule.quantityFormula) {
      quantity = evaluateQuantityFormula(rule.quantityFormula, product, attributeValues);
    } else if (typeof rule.quantity === 'number') {
      quantity = rule.quantity;
    }

    // Calculate price
    const unitPrice = rule.price || 0;
    const totalItemPrice = unitPrice * quantity;

    // Determine image URL (from linkedProduct or rule itself)
    const imageUrl = rule.linkedProduct?.imageUrl || rule.imageUrl || null;

    // Add to result - including product link info for backend differentiation
    const item = {
      id: rule.id,
      name: rule.name,
      sku: rule.sku,
      quantity,
      unitPrice,
      totalPrice: totalItemPrice,
      showOnQuote: rule.showOnQuote,
      showOnPackingSlip: rule.showOnPackingSlip,
      quotePlacement: rule.quotePlacement === 'configuration' ? 'configuration' : 'extra_option',
      // Product link info for backend to differentiate product vs manual
      sourceType: rule.sourceType || 'manual',
      productId: rule.productId || null,
      linkedProduct: rule.linkedProduct || null,
      imageUrl,
    };

    result.items.push(item);
    result.totalPrice += totalItemPrice;

    // Add to breakdown for display
    if (totalItemPrice > 0 || rule.showOnQuote) {
      result.breakdown.push({
        type: 'auto-include',
        label: rule.name,
        displayValue: `${quantity}x`,
        value: totalItemPrice, // Total price (unitPrice × quantity)
        price: unitPrice, // Unit price (consistent with upsells)
        quantity,
        unitPrice,
        sku: rule.sku,
        showOnQuote: rule.showOnQuote,
        showOnPackingSlip: rule.showOnPackingSlip,
        quotePlacement: rule.quotePlacement === 'configuration' ? 'configuration' : 'extra_option',
        // Product link info for backend to differentiate product vs manual
        sourceType: rule.sourceType || 'manual',
        productId: rule.productId || null,
        linkedProduct: rule.linkedProduct || null,
        imageUrl,
      });
    }

    // Add to rules arrays for quote display
    const ruleObj = {
      label: rule.name,
      value: totalItemPrice,
      type: 'add',
      displayValue: `${quantity}x ${rule.name}`,
      action: { type: 'add', value: totalItemPrice },
      rule: {
        label: rule.name,
        showInQuote: rule.showOnQuote,
      },
    };

    if (rule.showOnQuote) {
      shownRules.push(ruleObj);
    } else {
      hiddenRules.push(ruleObj);
    }

  });

  return result;
}

/**
 * Evaluate auto-include conditions
 *
 * @param {Object} rule - The auto-include rule
 * @param {Object} product - The product
 * @param {Object} attributeValues - Form values
 * @returns {boolean} Whether the rule should be applied
 */
function evaluateAutoIncludeConditions(rule, product, attributeValues) {
  // Always trigger
  if (rule.trigger === 'always') {
    return true;
  }

  // Conditional trigger - all conditions must match
  if (rule.trigger === 'conditional' && rule.conditions && rule.conditions.length > 0) {
    
    const allMatch = rule.conditions.every((condition, idx) => {
      const fieldId = `${product.id}-${condition.optionId}-input`;
      const value = attributeValues[fieldId];
      

      let result = false;
      switch (condition.type) {
        case 'config_option_equals': {
          result = value === condition.choiceId || value === condition.value;
          break;
        }

        case 'config_option_min': {
          const numValue = parseFloat(value);
          result = !isNaN(numValue) && numValue >= condition.minValue;
          break;
        }

        case 'config_option_max': {
          const numValue = parseFloat(value);
          result = !isNaN(numValue) && numValue <= condition.maxValue;
          break;
        }

        case 'config_option_between': {
          const numValue = parseFloat(value);
          result = !isNaN(numValue) && numValue >= condition.minValue && numValue <= condition.maxValue;
          break;
        }

        default:
          result = false;
      }
      
      return result;
    });
    
    return allMatch;
  }

  return false;
}

/**
 * Evaluate quantity formula
 *
 * @param {string} formula - Formula like "Math.ceil({breedte} / 100)"
 * @param {Object} product - The product
 * @param {Object} attributeValues - Form values
 * @returns {number} Calculated quantity
 */
function resolveQuantityFormulaValue(optionId, product, attributeValues, formulaContext) {
  const fieldId = `${product.id}-${optionId}-input`;
  const contextValue = formulaContext[optionId] ?? formulaContext[fieldId];
  if (contextValue !== undefined && contextValue !== null && contextValue !== '') {
    return contextValue;
  }

  const directValue = attributeValues[fieldId];
  if (directValue !== undefined && directValue !== null && directValue !== '') {
    return directValue;
  }

  const option = product.options?.find(
    (o) => o.label?.toLowerCase() === optionId.toLowerCase() || o.id === optionId
  );
  if (option) {
    const optionFieldId = `${product.id}-${option.id}-input`;
    return formulaContext[option.id] ?? attributeValues[optionFieldId];
  }

  const computedFields = product?.computedFields || product?.computed_fields || [];
  const computed = computedFields.find(
    (field) => field.id === optionId || field.label?.toLowerCase() === optionId.toLowerCase()
  );
  if (computed) {
    return formulaContext[computed.id];
  }

  return undefined;
}

function evaluateQuantityFormula(formula, product, attributeValues) {
  try {
    const formulaContext = buildPricingFormulaContext(product, attributeValues);
    const result = evaluateNumericFormula(formula, (optionId) =>
      resolveQuantityFormulaValue(optionId, product, attributeValues, formulaContext)
    );
    if (result === null || !Number.isFinite(result)) return 1;
    return Math.max(1, Math.round(result));
  } catch (error) {
    console.error('❌ Auto-include formula error:', error);
    return 1;
  }
}
